import React, { useState, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Box,
  Skeleton,
} from '@mui/material';

// Import Check and Close icons for edit mode
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// Import the Action icon from the same path as in DynamicTable
import Action_icon from '../../images/table/Actions_icon.svg';

import "../common/DynamicTable.css";
import { useTranslation } from 'react-i18next';

const RoleTable = ({
  headers,
  data,
  onActionClick,
  actions,
  loading,
  editableRow,
  editableFields,
  onEditChange,
  onEditSave,
  onEditCancel,
  skeletonRowCount = 5
}) => {
  const [activeRow, setActiveRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: '100%', bottom: 'auto' });
  const actionMenuRef = useRef(null);
  const actionIconRefs = useRef({});
  const { t } = useTranslation();

  // Close action menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActiveRow(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleActionClick = (event, rowIndex) => {
    event.stopPropagation();
    
    // If we're opening a menu (not closing the current one)
    if (activeRow !== rowIndex) {
      const iconElement = actionIconRefs.current[rowIndex];
      if (iconElement) {
        // Get the position of the clicked icon
        const rect = iconElement.getBoundingClientRect();
        // Get the viewport height
        const viewportHeight = window.innerHeight;
        // Calculate available space below
        const spaceBelow = viewportHeight - rect.bottom;
        
        // If there's not enough space below (less than 200px), position menu above
        if (spaceBelow < 200) {
          setMenuPosition({ top: 'auto', bottom: '100%' });
        } else {
          setMenuPosition({ top: '100%', bottom: 'auto' });
        }
      }
    }
    
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const handleActionItemClick = (actionId, rowIndex) => {
    if (onActionClick) {
      // Find the action object by ID to get its path
      const actionObj = actions.find(action => action.id === actionId);
      // Pass actionId, rowIndex, and the full action object (which contains the path)
      onActionClick(actionId, rowIndex, actionObj);
    }
    setActiveRow(null);
  };

  // Enhanced field change handler to properly handle empty values
  const handleFieldChange = (e, field) => {
    // This ensures empty values are properly handled
    const value = e.target.value;
    onEditChange(field, value);
  };

  // Render table cell - either as editable or static
  const renderCell = (rowIndex, field, value) => {
    if (editableRow === rowIndex && editableFields.includes(field)) {
      return (
        <TextField
          value={value || ""} // Add fallback empty string to handle null/undefined
          onChange={(e) => handleFieldChange(e, field)}
          variant="standard"
          fullWidth
          size="small"
          autoFocus={field === editableFields[0]} // Focus the first editable field
          sx={{
            background: "#F2F2F3",
            borderBottom: " 2px solid #A5DCF3",
            boxShadow: "-2px 4px 10px 0px #00000026",
            padding: "12px 0px 12px 20px"
          }}
        />
      );
    }
    return value;
  };

  const renderActions = (rowIndex) => {
    // If this row is in edit mode, show save and cancel buttons
    if (editableRow === rowIndex) {
      return (
        <div className="action-container" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div className="edit-actions" style={{ display: 'flex', gap: '4px' }}>
            <Tooltip title="Save">
              <IconButton
                sx={{
                  background: " #20A8E0",
                  color: 'white',
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  minHeight: '48px',
                  padding: '12px',
                  boxShadow: "-2px 4px 10px 0px #00000026",
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
                onClick={() => onEditSave(rowIndex)}
                size="small"
              >
                <CheckIcon fontSize="small" sx={{ fontSize: '24px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                sx={{
                  background: " #EE3B2B",
                  color: 'white',
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  minHeight: '48px',
                  padding: '12px',
                  boxShadow: "-2px 4px 10px 0px #00000026",
                  '&:hover': {
                    backgroundColor: '#c62828',
                  }
                }}
                onClick={onEditCancel}
                size="small"
              >
                <CloseIcon fontSize="small" sx={{ fontSize: '24px' }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      );
    }

    // Otherwise show action icon with dropdown menu
    return (
      <div className="action-container" style={{ position: "relative" }}>
        <div
          className="action-icon"
          onClick={(e) => handleActionClick(e, rowIndex)}
          ref={el => actionIconRefs.current[rowIndex] = el}
        >
          <img style={{ width: "20px", height: "20px" }} src={Action_icon} alt="Actions" />
        </div>
        {activeRow === rowIndex && (
          <div
            className="action-menu"
            ref={actionMenuRef}
            style={{
              ...menuPosition,
              minWidth: "160px",
              position: "absolute",
              right: 0,
              backgroundColor: "white",
              borderRadius: "5px",
              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
              zIndex: 100,
              whiteSpace: "nowrap"
            }}
          >
            {actions.map((action) => (
              <div
                key={action.id}
                className={`action-menu-item ${action.className || ''}`}
                onClick={() => handleActionItemClick(action.id, rowIndex)}
                style={{
                  padding: "10px 15px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  borderBottom: action === actions[actions.length - 1] ? "none" : "1px solid #f0f0f0"
                }}
              >
                <img
                  src={action.icon}
                  alt={action.label}
                  style={{ marginRight: "8px", width: "16px", height: "16px" }}
                />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {action.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render skeleton loaders for table rows
  const renderSkeletonRows = () => {
    return Array(skeletonRowCount).fill(0).map((_, rowIndex) => (
      <TableRow
        key={`skeleton-${rowIndex}`}
        sx={{
          padding: "15px 20px",
          "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
          borderBottom: "1px solid #ddd",
          height: "50px",
        }}
      >
        {headers.map((header, colIndex) => (
          <TableCell key={`skeleton-${rowIndex}-${colIndex}`} align={header === "Actions" ? "right" : "left"} sx={{ padding: "15px 20px" }}>
            {header === "Actions" ? (
              <Skeleton variant="circular" width={20} height={20} sx={{ ml: "auto" }} />
            ) : (
              <Skeleton variant="text" width={`${Math.floor(Math.random() * 40) + 60}%`} />
            )}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <TableContainer
      // component={Paper}
      sx={{
        marginTop: "20px",
        overflow: "auto",
        borderRadius: "10px",
        height: "460px",
        display: "flex",
        flexDirection: "column",
        elevation: 2,

        // Custom scrollbar styling for Chrome
        "&::-webkit-scrollbar": {
          width: "10px",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#647490",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#B7BFCC",
          borderRadius: "10px",
          margin: "5px",
        },

        // For Firefox
        scrollbarWidth: "thin",
        scrollbarColor: "#647490 #B7BFCC",
      }}
    >
      <Table sx={{ flex: 1, tableLayout: "fixed" }} size="small" aria-label="role management table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#647490",
              position: "relative",
            }}
          >
            {headers.map((header, index) => (
              <TableCell
                key={index}
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontWeight: "bold",
                  textAlign: header === "Actions" ? "right" : "left",
                  borderTopLeftRadius: index === 0 ? "10px" : 0,
                  borderTopRightRadius: index === headers.length - 1 ? "10px" : 0,
                  borderBottomLeftRadius: index === 0 ? "10px" : 0,
                  borderBottomRightRadius: index === headers.length - 1 ? "10px" : 0,
                  border: "none",
                }}
              >
                {loading ? <Skeleton variant="text" sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} /> : t(header)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody sx={{ height: "100%" }}>
          {loading ? (
            // Show skeleton while loading
            renderSkeletonRows()
          ) : data.length > 0 ? (
            data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  padding: "15px 20px",
                  "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                  fontWeight: "bold",
                  color: "black",
                  borderBottom: "1px solid #ddd",
                  height: "50px",
                  ...(editableRow === rowIndex && { backgroundColor: (theme) => theme.palette.action.selected })
                }}
              >
                {headers.map((header, colIndex) => (
                  <TableCell key={`${rowIndex}-${colIndex}`} align={header === "Actions" ? "right" : "left"} sx={{ padding: "15px 20px" }}>
                    {header === "Actions" ? (
                      renderActions(rowIndex)
                    ) : (
                      renderCell(rowIndex, header, row[header])
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            // Empty state row
            <TableRow sx={{ height: "100%" }}>
              <TableCell
                colSpan={headers.length}
                sx={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  height: "100%"
                }}
              >
                {t("No roles found")}
              </TableCell>
            </TableRow>
          )}
          {/* Fill remaining space if needed */}
          {data.length > 0 && data.length < 9 && (
            <TableRow sx={{ height: `${(9 - data.length) * 50}px` }}>
              <TableCell colSpan={headers.length}></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RoleTable;